import * as ReactSentry from "@sentry/react";
import environment, { electronOnly } from "src/utils/environment";
import { getPathForFileName } from "src/modules/activity-log/lib/fs-helpers";
import * as actions from "src/modules/activity-log/duck/actions";
import {
  RPCResponses,
  RPCRequests,
  Message
} from "src/modules/communication-protobuf";
import { getVideoConstraints } from "src/modules/rtc/media-constraints";
import { store } from "src/store/store";
import debug from "src/modules/debug-logger";
import {
  cleanUpActivityLogEventFiles,
  getActivityLogFileUrl
} from "src/modules/activity-log/ajax/cloud";
import { isImageDark } from "src/utils/imageBrigthness";
import convertWebmToMp4 from "./convertWebmToMp4";
import { getCameraAndMicrophoneInfo } from "src/modules/baby-station/BabyStation/media-devices";

const VIDEO_DURATION = 10000; // 10 seconds
const { isBrowser } = environment;

const Sentry = isBrowser ? ReactSentry : window.require("@sentry/electron");
const log = window.require("electron-log");

const fs = window.require("fs");
const { writeFile, unlink } = fs?.promises || {};

const videoConstraints = getVideoConstraints({
  videoQuality: "HIGH"
}).constraints.video;
const mediaResolution = `${videoConstraints.width.ideal}x${videoConstraints.height.ideal}`;
const mediaRecorderOptions = {
  videoBitsPerSecond: 12288000,
  ...(!environment.isBrowser && { mimeType: "video/webm;codecs=H264" })
};

export default class MediaRecorder {
  constructor({
    rtcManager,
    rpc,
    getParents,
    activityLogUploader,
    getMonitoring,
    claimNextEventId,
    getFileName,
    notifyParents
  }) {
    this.autoVideoTimeout = 0;
    this.getParents = getParents;
    this.rtcManager = rtcManager;
    this.activityLogUploader = activityLogUploader;
    this.rpc = rpc;
    this.stream = null;
    this.videoTrack = null;
    this.timeoutId = null;
    this.duration = null;
    this.thumbnailData = null;
    this.recordingStartDate = null;
    this.getMonitoring = getMonitoring;
    this.claimNextEventId = claimNextEventId;
    this.getFileName = getFileName;
    this.notifyParents = notifyParents;
    this.mediaRecorder = null;
    this.isCapturingManualVideo = false;
    this.state = "initializing";
    this.rpc.on(
      RPCRequests.StartVideoRecording.name,
      this.handleStartVideoRecording
    );
    this.rpc.on(
      RPCRequests.StopVideoRecording.name,
      this.handleStopVideoRecording
    );
    this.rpc.on(
      RPCRequests.TakeVideoSnapshot.name,
      this.handleTakeVideoSnapshot
    );
    this.rpc.on(
      RPCRequests.DeleteActivityLogEvent.name,
      this.deleteActivityLogEvent
    );
  }

  initialize = async () => {
    const { cameraList } = await getCameraAndMicrophoneInfo();
    const deviceId = cameraList?.[0]?.deviceId;

    await this.updateMediaRecorder(deviceId);
  };

  updateMediaRecorder = async (deviceId) => {
    if (this.stream) {
      this.videoTrack = this.stream.getVideoTracks()[0];
      this.videoTrack.stop();
      this.stream.removeTrack(this.videoTrack);
    }
    this.stream = await this.getAudioVideoStream(deviceId);
    this.mediaRecorder = new window.MediaRecorder(
      this.stream,
      mediaRecorderOptions
    );
    this.state = "ready";
  };

  deleteActivityLogEvent = async (request, from, sendResponse) => {
    const { eventId } = request;
    const monitoring = this.getMonitoring();

    const currentEvent = monitoring.events.filter(
      (event) => event.id === eventId
    )[0];

    if (!currentEvent) return;
    await cleanUpActivityLogEventFiles({
      eventType: currentEvent.eventType,
      fileName: currentEvent.fileName
    });
    const response = RPCResponses.DeleteActivityLogEvent.create();
    this.updateActivityLog({
      event: currentEvent,
      action: "delete"
    });
    sendResponse(response);
    const eventToRemove = monitoring.events
      .map((event) => event.id)
      .indexOf(eventId);
    monitoring.events.splice(eventToRemove, 1);
  };

  handleStartVideoRecording = (request, from, sendResponse) => {
    if (this.rtcManager.getPeer(from) == null) return;
    if (
      this.mediaRecorder.state === "recording" &&
      this.isCapturingManualVideo
    ) {
      const response = RPCResponses.StartVideoRecording.create({
        result: "ALREADY_RUNNING",
        recordingLimit: null
      });
      sendResponse(response);
    } else {
      this.startRecording({ parent: from });
      const response = RPCResponses.StartVideoRecording.create({
        result: "SUCCESS",
        recordingLimit: 10000
      });
      sendResponse(response);
    }
  };

  handleStopVideoRecording = async (request, from, sendResponse) => {
    if (this.rtcManager.getPeer(from) == null) return;
    if (this.mediaRecorder.state === "inactive") {
      const response = RPCResponses.StopVideoRecording.create({
        result: "ALREADY_STOPPED",
        event: {},
        thumbnailData: null
      });
      sendResponse(response);
      return;
    }
    clearTimeout(this.timeoutId);
    const fileName = this.getFileName("al-manual-video");
    try {
      const result = await this.stopRecording(fileName);
      const messageStructure = this.finishVideoEvent({
        id: this.claimNextEventId(),
        result,
        duration: this.duration,
        eventType: "MANUAL_VIDEO",
        fileName
      });
      const response = RPCResponses.StopVideoRecording.create(messageStructure);
      sendResponse(response);
    } catch (err) {
      log.error(err);
      Sentry?.captureMessage(`Failed recording ${err.message}`);
    }
  };

  finishVideoEvent = ({
    id,
    result,
    duration,
    eventType,
    fileName,
    referenceId
  }) => {
    const monitoring = this.getMonitoring();
    const messageStructure = {
      result: result ? "SUCCESS" : "FAILURE",
      event: {
        id,
        duration: duration,
        eventType,
        fileName,
        payload: JSON.stringify({ imageSize: mediaResolution }),
        fileRelatedLength: duration,
        fileRelatedDuration: duration,
        start: Date.now() - +monitoring.startDate,
        referenceId
      },
      thumbnailData: this.thumbnailData
    };

    if (result) {
      monitoring.events.push(messageStructure.event);
      this.activityLogUploader.uploadCaptureEvent({
        eventType,
        fileName,
        monitoringId: monitoring.monitoringId,
        rawData: result
      });
      store.dispatch(
        actions.eventEnded({
          monitoringId: monitoring.monitoringId,
          event: messageStructure.event
        })
      );
      this.updateActivityLog({
        event: messageStructure.event,
        action: "create"
      });
    }
    return messageStructure;
  };

  handleTakeVideoSnapshot = async (request, from, sendResponse) => {
    if (this.rtcManager.getPeer(from) == null) return;

    this.takeSnapshot({
      onFinish: ({ snapshot, event }) => {
        const response = RPCResponses.TakeVideoSnapshot.create({
          result: snapshot ? "SUCCESS" : "FAILURE",
          event,
          data: snapshot
        });
        sendResponse(response);
      },
      onError: () => {
        const response = RPCResponses.TakeVideoSnapshot.create({
          result: "FAILURE",
          event: {},
          data: null
        });
        sendResponse(response);
      },
      eventType: "MANUAL_PHOTO"
    });
  };
  // is used in EventManager without the params
  takeSnapshot = async ({ onError, onFinish, eventType, referenceId } = {}) => {
    const snapshot = await this.createThumbnail();
    const monitoring = this.getMonitoring();
    const fileSuffix = eventType.split("_")[0].toLowerCase();
    const fileName = this.getFileName(`al-${fileSuffix}-photo`);
    await electronOnly(async () => {
      try {
        await writeFile(getPathForFileName(fileName), snapshot);
      } catch (err) {
        debug.error("failed saving snapshot: ", err);
        unlink(getPathForFileName(fileName));
        if (onError) onError();
        return;
      }
    });
    const event = {
      id: this.claimNextEventId(),
      duration: 0,
      eventType,
      fileName,
      payload: JSON.stringify({ imageSize: mediaResolution }),
      fileRelatedLength: 0,
      fileRelatedDuration: 0,
      start: Date.now() - +monitoring.startDate,
      referenceId
    };

    monitoring.events.push(event);
    this.activityLogUploader.uploadCaptureEvent({
      eventType: event.eventType,
      fileName: event.fileName,
      monitoringId: monitoring.monitoringId,
      rawData: snapshot
    });
    store.dispatch(
      actions.eventEnded({
        monitoringId: monitoring.monitoringId,
        event
      })
    );
    if (onFinish) onFinish({ snapshot, event });
    this.updateActivityLog({ event, action: "create" });
  };

  createThumbnail = async () => {
    const video = window.videoRef.current;
    const canvas = window.document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0);
    const arrayBuffer = await new Promise((resolve) => {
      canvas.toBlob(async (blob) => {
        const buffer = await blob.arrayBuffer();
        if (!buffer) resolve(null);
        resolve(buffer);
      }, "image/jpeg");
    });
    canvas.remove();
    return arrayBuffer ? new Uint8Array(arrayBuffer) : null;
  };

  createNotificationImageUrl = async ({ activity, monitoringId }) => {
    const snapshot = await this.createThumbnail();
    const fileName = this.getFileName(`al-${activity.toLowerCase()}-photo`);

    const isTooDark = await isImageDark(snapshot);
    if (isTooDark) return;
    await electronOnly(async () => {
      try {
        await writeFile(getPathForFileName(fileName), snapshot);
      } catch (err) {
        debug.error("failed saving snapshot: ", err);
        unlink(getPathForFileName(fileName));
        return;
      }
    });

    return new Promise((resolve) => {
      this.activityLogUploader.uploadCaptureEvent(
        {
          eventType: `${activity}_PHOTO`,
          fileName,
          monitoringId,
          rawData: snapshot
        },
        async ({ result }) => {
          if (result !== "SUCCESS") return null;
          try {
            const { signedUrl } = await getActivityLogFileUrl({
              fileName,
              fileExtension: "jpg"
            });
            resolve(signedUrl);
          } catch (err) {
            debug.error("failed getting signed url for snapshot: ", err);
            resolve(null);
          }
        }
      );
    });
  };

  startRecording = async ({ parent, eventType, referenceId }) => {
    const now = Date.now();
    const isManual = Boolean(parent);
    if (!isManual && this.autoVideoTimeout + 30000 > now) return;
    this.autoVideoTimeout = Date.now();

    this.isCapturingManualVideo = isManual;
    if (this.mediaRecorder.state === "recording" && !isManual) return;
    const duration = VIDEO_DURATION;
    let extendDuration = false;
    if (this.mediaRecorder.state === "recording" && isManual) {
      clearTimeout(this.timeoutId);
      extendDuration = true;
    }

    if (isManual) this.thumbnailData = await this.createThumbnail();
    if (this.mediaRecorder.state !== "recording") {
      this.mediaRecorder.start();
      this.recordingStartDate = new Date();
    }
    const fileSuffix = isManual
      ? "manual"
      : eventType.split("_")[0].toLowerCase();
    const fileName = this.getFileName(`al-${fileSuffix}-video`);
    this.timeoutId = setTimeout(async () => {
      try {
        const result = await this.stopRecording(fileName);
        const messageStructure = this.finishVideoEvent({
          id: this.claimNextEventId(),
          result,
          duration: extendDuration ? this.duration : duration,
          eventType: isManual ? "MANUAL_VIDEO" : eventType,
          fileName,
          referenceId
        });
        if (isManual) {
          const message =
            Message.VideoRecordingDidFinishMessage.create(messageStructure);
          this.rtcManager.messenger.sendAsEnvelope({
            to: parent,
            payload: { message }
          });
        }
      } catch (err) {
        log.error(err);
        Sentry?.captureMessage(`Failed recording ${err.message}`);
      }
    }, duration);
  };

  stopRecording = async (fileName) => {
    const mediaRecorder = this.mediaRecorder;
    return await new Promise((resolve, reject) => {
      mediaRecorder.ondataavailable = async ({ data }) => {
        mediaRecorder.ondataavailable = null;
        const arrayBuffer = await data.arrayBuffer();
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          resolve(0);
        }
        if (environment.isBrowser) {
          resolve(new Uint8Array(arrayBuffer));
        }
        await electronOnly(async () => {
          if (arrayBuffer) {
            try {
              await convertWebmToMp4({
                webmData: arrayBuffer,
                fileName
              });
              resolve(1);
            } catch (err) {
              resolve(0);
            }
          }
        });
      };
      if (this.mediaRecorder.state === "inactive") {
        reject("media recorder in inactive state");
        return;
      }
      mediaRecorder.stop();
      this.isCapturingManualVideo = false;
      this.duration = new Date() - this.recordingStartDate;
    });
  };

  getAudioVideoStream = async (deviceId) => {
    const videoConstraints = getVideoConstraints({
      videoQuality: "HIGH",
      videoOptions: { deviceId }
    }).constraints;
    const videoConstraintsWithAudio = { ...videoConstraints, audio: true };

    const stream = await navigator.mediaDevices.getUserMedia(
      videoConstraintsWithAudio
    );
    return stream;
  };

  updateActivityLog = async ({ event, action }) => {
    const parents = this.getParents();
    const message = Message.ActivityLogUpdateMessage.create(
      action === "create" ? { eventToCreate: event } : { eventToDelete: event }
    );
    this.notifyParents({ parents, message });
  };

  destroy = () => {
    if (this.stream) this.videoTrack = this.stream.getVideoTracks()[0];
    if (this.videoTrack) {
      this.videoTrack.stop();
      this.stream.removeTrack(this.videoTrack);
    }

    if (this.stream) {
      const tracks = this.stream.getTracks();
      tracks.forEach((track) => {
        track.stop();
        track.enabled = false;
      });
    }

    this.rpc.off(
      RPCRequests.StartVideoRecording.name,
      this.handleStartVideoRecording
    );
    this.rpc.off(
      RPCRequests.StopVideoRecording.name,
      this.handleStopVideoRecording
    );
    this.rpc.off(
      RPCRequests.TakeVideoSnapshot.name,
      this.handleTakeVideoSnapshot
    );
  };
}
